import { Card, CardContent, CardHeader } from '@/common/components/Card.tsx'
import Label from '@/common/components/Label.tsx'
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/shad/ui/command.tsx'
import { RadioGroup, RadioGroupItem } from '@/shad/ui/radio-group.tsx'
import React, { useState } from 'react'
import AddCategoryPopup from './AddCategoryPopup.tsx'
import EditCategoryPopup from './EditCategoryPopup.tsx'
import Badge from '@/common/components/Badge.tsx'
import {
  SYSTEM_CATALOG_ITEM_STATUSES,
  SystemCatalogItemStatuses,
} from '@/pages/setup/provider_categories/constans.ts'
import { ProviderCategory } from '@/queries/providers/categories/schema.ts'

interface Props {
  categories: ProviderCategory[]
  onCategoryChange?: () => void
  invalidateCategories: () => void
}

function CategoryList(props: Props) {
  const { categories, onCategoryChange, invalidateCategories } = props
  const [searchQuery, setSearchQuery] = useState<string>('')
  const [selectedCategoryStatus, setSelectedCategoryStatus] =
    useState<SystemCatalogItemStatuses>('All')

  const filteredCategories = React.useMemo(() => {
    return categories.filter((category) => {
      const matchesSearch =
        searchQuery === '' ||
        category.name.toLowerCase().includes(searchQuery.toLowerCase())

      const matchesStatus =
        selectedCategoryStatus === 'All' ||
        category.status === selectedCategoryStatus

      return matchesSearch && matchesStatus
    })
  }, [categories, searchQuery, selectedCategoryStatus])

  const handleCategoryChange = () => {
    if (onCategoryChange) {
      onCategoryChange()
    }
    invalidateCategories()
  }

  const handleRadioChange = (status: SystemCatalogItemStatuses) => {
    setSelectedCategoryStatus(status)
  }

  return (
    <Card className="">
      <CardHeader className="py-2">
        <div className="flex items-center justify-between">
          <h3 className="font-medium">Categories</h3>
          <div>
            <AddCategoryPopup
              categories={categories}
              onAddCategorySuccess={handleCategoryChange}
            />
          </div>
        </div>
      </CardHeader>
      <CardContent className="w-full">
        <div className="md:flex gap-8 h-full w-full">
          <div className="w-full ">
            <Command className="h-full ">
              <div className="border-b pb-4 border-gray-200">
                <CommandInput
                  placeholder="Search..."
                  value={searchQuery}
                  onValueChange={setSearchQuery}
                />
                <div className="">
                  <RadioGroup
                    onValueChange={(value) => {
                      function isValidStatus(
                        string: string
                      ): string is SystemCatalogItemStatuses {
                        return SYSTEM_CATALOG_ITEM_STATUSES.includes(
                          string as SystemCatalogItemStatuses
                        )
                      }

                      if (!isValidStatus(value)) {
                        throw new Error('Failed to add status')
                      }

                      // TypeScript now knows value is SystemCatalogItemStatuses
                      handleRadioChange(value)
                    }}
                    defaultValue={selectedCategoryStatus}
                    className="flex space-x-4 mt-4"
                  >
                    {SYSTEM_CATALOG_ITEM_STATUSES.map((status) => {
                      return (
                        <div key={status} className="flex space-x-1 text-sm">
                          <RadioGroupItem id={status} value={status} />
                          <Label htmlFor={status}>{status}</Label>
                        </div>
                      )
                    })}
                  </RadioGroup>
                </div>
              </div>
              <div className="h-full w-full">
                <CommandList className="w-full h-full py-2 min-h-[30rem] max-h-[30rem]">
                  <CommandEmpty>No results found.</CommandEmpty>
                  {/* Use our filtered categories instead of all categories */}
                  {filteredCategories.map((category) => {
                    return (
                      <CommandItem
                        key={category.id}
                        value={category.name.toString()}
                        className="flex items-center justify-between gap-3 text-gray-600 hover:text-gray-600 data-[selected=true]:text-gray-600 data-[selected=true]:bg-transparent"
                      >
                        <div className="flex-1 min-w-0">
                          <div
                            className="font-medium text-gray-800 truncate"
                            title={category.name}
                          >
                            {category.name}
                          </div>
                        </div>

                        <div className="flex items-center gap-2 flex-shrink-0">
                          <Badge
                            semanticColor={
                              category.status === 'Active'
                                ? 'Positive'
                                : 'Negative'
                            }
                          >
                            {category.status}
                          </Badge>
                          <EditCategoryPopup
                            category={category}
                            onAddCategorySuccess={invalidateCategories}
                          />
                        </div>
                      </CommandItem>
                    )
                  })}
                </CommandList>
              </div>
            </Command>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

export default CategoryList
